import { Button, Flex } from "@aws-amplify/ui-react"
import type { UserDataDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { Link } from "react-router-dom"
import { getLinkToLinkMachine } from "../../LinkUtils"
import { NoticeBanner } from "../MessageBanner"
import { useSessionSnooze } from "./useSessionSnooze"

interface OnboardingSlotProps {
  userData: UserDataDto | undefined
}

/**
 * Shows the next thing a user should do, above the page content.
 *
 * Every item is a **one-line notice with one action**, never a form: a banner
 * is only recognisable as a banner at that size, and the work itself belongs
 * on a page of its own (see `routes/orgs/LinkMachine.tsx`).
 *
 * Deliberately not a gate, unlike `<Registration />`: a registered user is a
 * precondition for everything, a linked machine is not. Recipes, feed
 * components and org settings all work without one, so this never blocks a
 * route.
 *
 * One item today. A second one ("create your first recipe") is another
 * condition plus another snooze key below. An announcement ("what's new in
 * version N") belongs here too, but needs a persisted "last seen version"
 * rather than the session snooze — see
 * `docs/issues/link-machine-discoverability.md`.
 */
export const OnboardingSlot = ({ userData }: OnboardingSlotProps) => {
  const linkMachine = useSessionSnooze("link-machine")

  // Only the freshly registered case: one org, and nothing linked to it yet.
  const orgs = userData?.Organizations
  const singleOrg = orgs?.length === 1 ? orgs[0] : undefined

  if (!singleOrg || singleOrg.Machines?.length || linkMachine.isSnoozed) {
    return null
  }

  return (
    <div className="px-7 pt-4">
      <NoticeBanner>
        <Flex
          alignItems={{ base: "flex-start", small: "center" }}
          justifyContent="space-between"
          direction={{ base: "column", small: "row" }}
          gap="xs"
        >
          <div>
            {I18n.get(
              "Ihre Maschine ist noch nicht mit Ihrem Betrieb verknüpft. Erst danach sehen Sie ihre Daten hier."
            )}
          </div>
          {/* Dismissive action first, primary last (the usual banner order),
              with enough gap that "Später" is not a mis-click away. */}
          <Flex gap="medium" alignItems="center">
            <Button size="small" variation="link" onClick={linkMachine.snooze}>
              {I18n.get("Später")}
            </Button>
            <Link to={getLinkToLinkMachine(singleOrg.id)}>
              <Button size="small" variation="primary">
                {I18n.get("Maschine verknüpfen")}
              </Button>
            </Link>
          </Flex>
        </Flex>
      </NoticeBanner>
    </div>
  )
}
